import { useEffect, useState } from "react";

import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { FileDownloadOutlined } from "@mui/icons-material";
import { useParams } from "react-router-dom";

import { ButtonV1, Loader } from "components";

import { formatDate, validateErrorMsg, validateNull } from "utils";
import { useDownload, useToaster } from "hooks";
import { getCall_v2 } from "services";

import { error } from "constants";
import { ATTACHMENTS_TABLE_HEADERS } from "./constants";

const Attachments = () => {
  const [isAttachmentDataLoading, setIsAttachmentDataLoading] = useState(false);
  const [attachmentViewModal, setAttachmentViewModal] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const theme = useTheme();
  const orderNo = useParams();
  const { downloadFile } = useDownload();

  const triggerToaster = useToaster();

  const getAttachmentData = async () => {
    try {
      const data = await getCall_v2(
        `/joms/api/fetch-orders-attachments/${orderNo.id}`,
      );
      if (data) {
        setAttachments(data);
      }
    } catch (err) {
      triggerToaster(validateErrorMsg(err.message), error);
    } finally {
      setIsAttachmentDataLoading(false);
    }
  };

  useEffect(() => {
    setIsAttachmentDataLoading(true);
    void getAttachmentData();
  }, []);

  const handleViewFile = async (row) => {
    const blob = await getFileData(row.id, row.fileType);
    const fileBlob = new Blob([blob], {
      type: "application/pdf",
    });

    const url = URL.createObjectURL(fileBlob);
    window.open(url, "_blank", "url");

    toggleAttachmentViewModal();
  };

  const getFileData = async (id, fileType) => {
    try {
      const file =
        fileType === "Order Summary"
          ? "orderSummary"
          : fileType.replace(/\s/g, "");
      const { data } = await getCall_v2(
        `/joms/api/fetch-orders-attachments/${orderNo.id}/${id}?fileType=${file}`,
        {},
        "blob",
      );
      return data;
    } catch (err) {
      triggerToaster(validateErrorMsg(err.message), error);
    }
  };

  const handleDownloadFile = async (row) => {
    triggerToaster("File download started", "success");
    const fileData = await getFileData(row.id, row.fileType);

    downloadFile(fileData, row.fileName);
  };

  const toggleAttachmentViewModal = () => {
    setAttachmentViewModal(!attachmentViewModal);
  };

  return (
    <Box>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {ATTACHMENTS_TABLE_HEADERS.map((item) => (
                <TableCell key={item}>{item}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isAttachmentDataLoading ? (
              <TableRow>
                <TableCell colSpan={ATTACHMENTS_TABLE_HEADERS.length}>
                  <Loader sx={{ margin: 0 }} />
                </TableCell>
              </TableRow>
            ) : attachments.data?.length ? (
              attachments.data.map((row) => {
                const { fileType, uploadedAt, uploadedBy, validity } = row;
                return (
                  <TableRow key={row?.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {fileType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {validateNull(formatDate(uploadedAt))}
                    </TableCell>
                    <TableCell>{validateNull(uploadedBy)}</TableCell>
                    <TableCell>{validateNull(formatDate(validity))}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <ButtonV1
                          size="small"
                          variant="text"
                          title="View file"
                          sx={{
                            minWidth: "auto",
                            padding: 0,
                            textTransform: "none",
                          }}
                          onClick={() => handleViewFile(row)}
                        />
                        <Divider
                          orientation="vertical"
                          color={theme.palette.grey["400"]}
                          variant="middle"
                          flexItem
                        />

                        <FileDownloadOutlined
                          fontSize="small"
                          sx={{
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                          }}
                          data-testid="download-icon"
                          onClick={() => handleDownloadFile(row)}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={ATTACHMENTS_TABLE_HEADERS.length}
                  align="center"
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Attachments;
